/**
 * CEX Symbol Status Cache Storage
 *
 * IndexedDB/localStorage cache for per-symbol "status" metadata (currently: ST zone flag).
 * This is used to avoid spamming exchange APIs from UI consumers (sidebar, pair-search-nav, etc.).
 *
 * Design:
 * - Separate caches by network (mainnet/testnet) for safety, even though exchanges are global.
 * - Store one record per exchange containing a map of requested symbol keys to cached entries.
 * - Cache entries include an "ok" vs "error" status to keep error retries short.
 */

import localforage from 'localforage';

import { ECexName, isTestnet } from 'web3';

const STORE_NAME = 'cex-symbol-status-cache';
const STORE_CONFIG = {
  name: STORE_NAME,
  storeName: 'data',
};

/** Current schema version - increment when cache format changes */
const SCHEMA_VERSION = 1;

/** Default TTL for successful symbol status entries: 60 minutes */
export const DEFAULT_SYMBOL_STATUS_TTL_MS = 60 * 60 * 1000;

/** Shorter TTL for error-derived entries to allow quick recovery */
export const ERROR_SYMBOL_STATUS_TTL_MS = 5 * 60 * 1000;

export type SymbolStatusCacheEntry = {
  isSt: boolean;
  updatedAt: number;
  status: 'ok' | 'error';
};

export type SymbolStatusCacheEntries = Record<string, SymbolStatusCacheEntry>;

export interface SymbolStatusCacheData {
  version: number;
  entries: SymbolStatusCacheEntries;
}

const storage = localforage.createInstance(STORE_CONFIG);

const getNetworkKey = (): 'mainnet' | 'testnet' => (isTestnet ? 'testnet' : 'mainnet');

const buildCacheKey = (exchange: ECexName): string =>
  `cex-symbol-status:v${SCHEMA_VERSION}:${getNetworkKey()}:${exchange.toLowerCase()}`;

const ensureReady = async (): Promise<void> => {
  try {
    await storage.ready();
  } catch (error) {
    console.error('[SymbolStatusCacheStorage] IndexedDB not ready', error);
    throw error;
  }
};

const isValidEntry = (entry: unknown): entry is SymbolStatusCacheEntry => {
  if (!entry || typeof entry !== 'object') {
    return false;
  }

  const candidate = entry as SymbolStatusCacheEntry;

  return (
    typeof candidate.isSt === 'boolean' &&
    typeof candidate.updatedAt === 'number' &&
    Number.isFinite(candidate.updatedAt) &&
    (candidate.status === 'ok' || candidate.status === 'error')
  );
};

export const isEntryFresh = (
  entry: SymbolStatusCacheEntry,
  ttlMs: number = DEFAULT_SYMBOL_STATUS_TTL_MS,
  errorTtlMs: number = ERROR_SYMBOL_STATUS_TTL_MS,
): boolean => {
  const now = Date.now();
  const effectiveTtl = entry.status === 'error' ? errorTtlMs : ttlMs;
  return now - entry.updatedAt < effectiveTtl;
};

export const loadSymbolStatusCache = async (
  exchange: ECexName,
): Promise<SymbolStatusCacheData | null> => {
  try {
    await ensureReady();
    const key = buildCacheKey(exchange);
    const cached = await storage.getItem<SymbolStatusCacheData>(key);

    if (!cached) {
      return null;
    }

    if (cached.version !== SCHEMA_VERSION) {
      await storage.removeItem(key);
      return null;
    }

    if (!cached.entries || typeof cached.entries !== 'object') {
      await storage.removeItem(key);
      return null;
    }

    const entries = cached.entries as Record<string, unknown>;
    const sanitized: SymbolStatusCacheEntries = {};

    for (const [entryKey, value] of Object.entries(entries)) {
      if (isValidEntry(value)) {
        sanitized[entryKey] = value;
      }
    }

    return {
      version: SCHEMA_VERSION,
      entries: sanitized,
    };
  } catch (error) {
    console.error('[SymbolStatusCacheStorage] Failed to load cache', { exchange, error });
    return null;
  }
};

export const saveSymbolStatusCache = async (
  exchange: ECexName,
  entries: SymbolStatusCacheEntries,
): Promise<void> => {
  try {
    await ensureReady();
    const key = buildCacheKey(exchange);
    const data: SymbolStatusCacheData = {
      version: SCHEMA_VERSION,
      entries,
    };

    await storage.setItem(key, data);
  } catch (error) {
    console.error('[SymbolStatusCacheStorage] Failed to save cache', { exchange, error });
  }
};

export const clearSymbolStatusCache = async (exchange: ECexName): Promise<void> => {
  try {
    await ensureReady();
    const key = buildCacheKey(exchange);
    await storage.removeItem(key);
  } catch (error) {
    console.error('[SymbolStatusCacheStorage] Failed to clear cache', { exchange, error });
  }
};

export const clearAllSymbolStatusCaches = async (): Promise<void> => {
  try {
    await ensureReady();
    const keys = await storage.keys();
    const cacheKeys = keys.filter(k => k.startsWith('cex-symbol-status:'));
    await Promise.all(cacheKeys.map(k => storage.removeItem(k)));
  } catch (error) {
    console.error('[SymbolStatusCacheStorage] Failed to clear all caches', { error });
  }
};
